/**
 * The card for one attached file — filename over a short descriptor. Used both
 * staged in the composer and above the user's bubble once sent.
 */

import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

/** Human-readable byte count for the card's tooltip. */
export const formatFileSize = (size: number): string => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

/** Short descriptor, from the extension — browsers disagree on spreadsheet MIME types. */
export const attachmentKindLabel = (filename: string): string => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return 'Spreadsheet';
  if (lower.endsWith('.csv')) return 'CSV';
  return 'File';
};

export interface AttachmentCardProps {
  filename: string;
  size: number;
  /** Composer-side upload state. Omitted in the transcript, where the file is stored. */
  status?: 'uploading' | 'ready' | 'error';
  /** Researcher-facing failure reason, shown in place of the descriptor. */
  error?: string;
  /** Renders a remove control when provided (composer only). */
  onRemove?: () => void;
  testId?: string;
}

export const AttachmentCard = ({
  filename,
  size,
  status,
  error,
  onRemove,
  testId,
}: AttachmentCardProps) => {
  const failed = status === 'error';
  const subtitle = failed ? error ?? "Couldn't upload" : attachmentKindLabel(filename);

  return (
    <Tooltip title={`${filename} — ${formatFileSize(size)}`}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        data-testid={testId ?? 'attachment-card'}
        sx={{
          display: 'inline-flex',
          maxWidth: 280,
          paddingX: 1.5,
          paddingY: 1,
          border: (theme) =>
            `1px solid ${failed ? theme.palette.error.main : theme.palette.divider}`,
          // Px string, not the bare token: `sx` multiplies a numeric borderRadius
          // by `theme.shape.borderRadius`, turning 12px into a 96px stadium.
          borderRadius: `${borderRadius.lg}px`,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        {status === 'uploading' ? (
          // `disableShrink` holds the arc at one length, so it reads as a plain
          // rotation; `flexShrink: 0` keeps a long filename from ovalling it.
          <CircularProgress
            size={16}
            thickness={4}
            disableShrink
            aria-label="Uploading"
            sx={{ flexShrink: 0 }}
          />
        ) : failed ? (
          <ErrorOutlineIcon fontSize="small" color="error" aria-hidden sx={{ flexShrink: 0 }} />
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {filename}
          </Typography>
          <Typography
            variant="caption"
            color={failed ? 'error' : 'text.secondary'}
            sx={{ display: 'block', lineHeight: 1.4 }}
          >
            {status === 'uploading' ? 'Uploading…' : subtitle}
          </Typography>
        </Box>
        {onRemove ? (
          <IconButton
            size="small"
            onClick={onRemove}
            aria-label={`Remove ${filename}`}
            data-testid="attachment-remove"
            sx={{ marginLeft: 'auto', flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
    </Tooltip>
  );
};
